/**
 * ConversationList Component - HYB Shop Chat
 * Container with search, header, and scrollable list
 */


import { MessageSquare, Search } from 'lucide-react';
import { cn } from '../../../utils/responsive';
import { EmptyState } from '../../ui/EmptyState';
import { ConversationListItem } from './ConversationListItem';
import { ConversationListSkeleton } from './ConversationListSkeleton';
import { ConversationStatus } from '../ConversationStatusBadge';

interface Conversation {
  conversationId: string;
  userId: string;
  userName?: string;
  phoneNumber?: string;
  isGuest?: boolean;
  guestId?: string;
  status?: string | ConversationStatus;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  unreadCount?: number;
  createdAt?: string;
  assignedAdminId?: string;
  lastMessageSender?: 'user' | 'admin';
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (conversation: Conversation) => void;
  onDelete?: (conversationId: string) => void;
  isLoading?: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  isUserOnline?: (userId: string) => boolean;
  className?: string;
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  onDelete,
  isLoading = false,
  searchTerm = '',
  onSearchChange,
  isUserOnline,
  className,
}: ConversationListProps) {
  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-stone-200 dark:border-gray-700 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-stone-900 dark:text-white">
          <MessageSquare className="h-4 w-4 text-amber-500" />
          รายการสนทนา
          {conversations.length > 0 && (
            <span className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              'bg-amber-100 text-amber-700',
              'dark:bg-amber-900/30 dark:text-amber-400'
            )}>
              {conversations.length}
            </span>
          )}
        </h2>
      </div>

      {/* Search */}
      <div className="relative p-4 border-b border-stone-100 dark:border-gray-700/50">
        <Search className={cn(
          'pointer-events-none absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2',
          'text-stone-400 dark:text-gray-500'
        )} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange?.(e.target.value)}
          placeholder="ค้นหาจากชื่อ, เบอร์โทร, หรือรหัส"
          className={cn(
            'w-full rounded-xl border border-stone-200 dark:border-gray-600',
            'bg-stone-50 dark:bg-gray-900',
            'py-2.5 pl-10 pr-4 text-sm',
            'text-stone-900 dark:text-gray-100',
            'placeholder:text-stone-400 dark:placeholder:text-gray-500',
            'transition-all duration-200',
            'focus:bg-white dark:focus:bg-gray-800',
            'focus:border-amber-400 dark:focus:border-amber-500',
            'focus:outline-none focus:ring-2 focus:ring-amber-400/20'
          )}
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <ConversationListSkeleton count={5} />
        ) : conversations.length === 0 ? (
          <EmptyState
            className="m-4"
            icon={<MessageSquare className="h-5 w-5" />}
            title={searchTerm ? 'ไม่พบรายการที่ค้นหา' : 'ยังไม่มีรายชื่อสนทนา'}
            description={
              searchTerm
                ? 'ลองค้นหาด้วยคำอื่น'
                : 'เมื่อมีลูกค้าทักเข้ามา รายชื่อจะแสดงในพื้นที่นี้'
            }
          />
        ) : (
          <ul className="divide-y divide-stone-100 dark:divide-gray-700/50">
            {conversations.map((conversation) => (
              <li key={conversation.conversationId}>
                <ConversationListItem
                  conversation={conversation}
                  isActive={selectedId === conversation.conversationId}
                  isOnline={isUserOnline?.(conversation.userId) ?? false}
                  onClick={() => onSelect(conversation)}
                  onDelete={() => onDelete?.(conversation.conversationId)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ConversationList;
